import { Controller } from "@hotwired/stimulus"
import { Chart, ArcElement, PieController, Tooltip, Legend } from "chart.js"

Chart.register(ArcElement, PieController, Tooltip, Legend)

export default class extends Controller {
  static targets = ["canvas", "icon", "label", "tooltip"]

  static values = {
    data: Array,
    showLabels: { type: Boolean, default: false },
  }

  connect() {
    this.createChart()
    this.positionIcons()
    if (this.showLabelsValue) {
      this.positionLabels()
    }
  }

  disconnect() {
    if (this.chart) {
      this.chart.destroy()
    }
  }

  createChart() {
    const ctx = this.canvasTarget.getContext("2d")
    const chartData = Array(this.dataValue.length).fill(1)
    const sliceColors = this.dataValue.slice(0, chartData.length)

    this.chart = new Chart(ctx, {
      type: "pie",
      data: {
        datasets: [{
          data: chartData,
          backgroundColor: sliceColors,
          borderColor: "#F5F2ED",
          borderWidth: 3,
          hoverBackgroundColor: sliceColors,
          hoverBorderColor: "#F5F2ED",
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: true,
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            enabled: false,
            position: 'nearest',
            external: (context) => this.externalTooltipHandler(context)
          }
        },
        animation: false
      }
    })
  }

  externalTooltipHandler(context) {
    const { tooltip } = context
    const icons = this.iconTargets

    // Remove hover class from all icons first
    icons.forEach((el) => {
      el.classList.remove("is-chart-hovered")
    })

    // If no tooltip should be shown, return early
    if (tooltip.opacity === 0) {
      return
    }

    // Get the index of the hovered slice
    const dataIndex = tooltip.dataPoints?.[0]?.dataIndex
    if (dataIndex === undefined || dataIndex >= icons.length) {
      return
    }

    // Add hover class to trigger tooltip via CSS
    const iconEl = icons[dataIndex]
    if (iconEl) {
      iconEl.classList.add("is-chart-hovered")
    }
  }

  positionIcons() {
    const icons = this.iconTargets
    if (icons.length === 0) return

    // Use equal-sized slices (matching createChart)
    const chartData = Array(this.dataValue.length).fill(1)
    const sliceColors = this.dataValue
    const total = chartData.length
    const container = this.element
    const containerRect = container.getBoundingClientRect()
    const centerX = containerRect.width / 2
    const centerY = containerRect.height / 2
    // Position icons inside the pie slices at 65% from center to edge
    const radius = Math.min(centerX, centerY) * 0.65

    // Chart.js pie starts at -90 degrees (12 o'clock) and goes clockwise
    let currentAngle = -Math.PI / 2

    icons.forEach((icon, index) => {
      if (index >= chartData.length) return

      // Calculate the angle for the center of this slice
      const sliceAngle = (chartData[index] / total) * 2 * Math.PI
      const midAngle = currentAngle + sliceAngle / 2

      // Calculate x, y position
      const x = centerX + radius * Math.cos(midAngle)
      const y = centerY + radius * Math.sin(midAngle)

      // Position the icon (offset by half its size to center it)
      icon.style.left = `${x}px`
      icon.style.top = `${y}px`
      icon.style.transform = "translate(-50%, -50%)"

      // Set icon color based on background brightness
      const sliceColor = sliceColors[index]
      const iconColor = this.isColorDark(sliceColor) ? "#FFFFFF" : "#1B2632"
      icon.style.color = iconColor

      // Move to next slice
      currentAngle += sliceAngle
    })
  }

  // Determine if a hex color is dark based on luminance
  isColorDark(hexColor) {
    // Default to dark if no color provided
    if (!hexColor) return false

    // Remove # if present
    const hex = hexColor.replace("#", "")

    // Parse RGB values
    const r = parseInt(hex.substring(0, 2), 16)
    const g = parseInt(hex.substring(2, 4), 16)
    const b = parseInt(hex.substring(4, 6), 16)

    // Calculate relative luminance (per WCAG)
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255

    // Return true if dark (luminance below 0.25)
    // Only truly dark colors like primary blue (#1B2632) get white icons
    return luminance < 0.25
  }

  positionLabels() {
    const labels = this.labelTargets
    if (labels.length === 0) return

    // Use equal-sized slices (matching createChart)
    const chartData = Array(this.dataValue.length).fill(1)
    const total = chartData.length
    const container = this.element
    const containerRect = container.getBoundingClientRect()
    const centerX = containerRect.width / 2
    const centerY = containerRect.height / 2
    // Position labels just outside the pie chart
    const radius = Math.min(centerX, centerY) * 1.2

    // Chart.js pie starts at -90 degrees (12 o'clock) and goes clockwise
    let currentAngle = -Math.PI / 2

    labels.forEach((label, index) => {
      if (index >= chartData.length) return

      // Calculate the angle for the center of this slice
      const sliceAngle = (chartData[index] / total) * 2 * Math.PI
      const midAngle = currentAngle + sliceAngle / 2

      // Calculate x, y position
      const x = centerX + radius * Math.cos(midAngle)
      const y = centerY + radius * Math.sin(midAngle)

      // Position the label (offset by half its size to center it)
      label.style.left = `${x}px`
      label.style.top = `${y}px`
      label.style.transform = "translate(-50%, -50%)"

      // Move to next slice
      currentAngle += sliceAngle
    })
  }
}
